import { useEffect, useContext } from 'react';
import { useRouter } from 'next/router';
import { AuthContext } from '../context/AuthContext';
import STTLayoutEnhanced from '../components/STTLayoutEnhanced';
import { useHealthCheck } from '../hooks/useHealthCheck';
import { useLanguage } from '../context/LanguageContext';
import TTS_SHARED from '../locales/tts-shared';

export default function SpeechToText() {
  const { isAuthenticated, loading, authToken } = useContext(AuthContext);
  const router = useRouter();
  const { isHealthy, isChecking, healthError, retryCount, checkHealth, isMaintenanceMode } = useHealthCheck();
  const { language } = useLanguage();
  const t = (TTS_SHARED[language] || TTS_SHARED.en).page;

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  // Don't render the STT component if not authenticated or still loading
  if (!isAuthenticated || loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show health check status if backend is not ready
  if (!isHealthy) {
    // Show maintenance mode screen if detected
    if (isMaintenanceMode) {
      return (
        <div className="flex items-center justify-center h-full bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="mb-6">
              <svg className="w-20 h-20 mx-auto text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
              {t.maintenance_h2}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t.maintenance_body}
            </p>
            <div className="flex items-center justify-center space-x-1 mb-6">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <button
              onClick={checkHealth}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
            >
              {t.check_status}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
              {t.thanks}
            </p>
          </div>
        </div>
      );
    }

    // Show regular connection error for other issues
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          {isChecking ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">
                {retryCount > 0 ? `${t.connecting_attempt_prefix} ${retryCount}${t.connecting_attempt_suffix}` : t.connecting}
              </p>
            </>
          ) : (
            <>
              <div className="text-red-600 dark:text-red-400 mb-4">
                <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="font-medium">{t.unable_connect}</p>
                {healthError && healthError !== 'maintenance' && <p className="text-sm mt-2">{healthError}</p>}
              </div>
              <button
                onClick={checkHealth}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                {t.retry_connection}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return <STTLayoutEnhanced />;
}
